import { Duration } from 'luxon';
import type { Maybe } from 'types/Utilities';

/** Format a number as currency */
function toCurrency(amountInCents: number, { includeDecimals = true } = {}) {
  const dollars = amountInCents / 100;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    ...(!includeDecimals
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : {}),
  }).format(dollars);
}

/**
 * Format a number compactly
 *
 * @example toCompact(1234) // 1.2K
 */
function toCompact(number: number) {
  return new Intl.NumberFormat(undefined, { notation: 'compact' }).format(
    number
  );
}

/**
 * Given a number of seconds, format it as a duration
 *
 * @example toDuration(93) // 1m 33s
 */
function toDuration(durationInSeconds: number) {
  const duration = Duration.fromObject({
    seconds: durationInSeconds,
  }).rescale();

  let format = "s's'";
  if (duration.minutes) format = "m'm' " + format;
  if (duration.hours) format = "h'h' " + format;

  return duration.toFormat(format);
}

/**
 * Format a number with two decimal places
 */
function toDecimal(number: number) {
  return number.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

type Style = 'strict-two' | 'integer' | 'max-two';
/**
 * Format a decimal number as a percentage
 * @param decimal - the decimal number to format
 * @param style - the output style
 *
 * @example decimalToPercent(0.126) // 12.60%
 * @example decimalToPercent(0.126, 'strict-two') // 12.60%
 * @example decimalToPercent(0.126, 'integer') // 13%
 * @example decimalToPercent(0.126, 'max-two') // 12.6%
 */
function decimalToPercent(decimal: number, style?: Style): string;
function decimalToPercent(decimal: Maybe<number>, style?: Style): Maybe<string>;
function decimalToPercent(
  decimal: Maybe<number>,
  style: Style = 'strict-two'
): Maybe<string> {
  if (typeof decimal !== 'number') return decimal;

  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    minimumFractionDigits: style === 'strict-two' ? 2 : 0,
    maximumFractionDigits: style === 'integer' ? 0 : 2,
  }).format(decimal);
}

/**
 * Format a number as an ordinal (1st, 2nd, 3rd, etc.)
 * @example toOrdinal(1) // 1st
 */
function toOrdinal(number: number) {
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;

  // Special cases for 11th, 12th, 13th
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${number}th`;
  }

  // Regular cases
  switch (lastDigit) {
    case 1:
      return `${number}st`;
    case 2:
      return `${number}nd`;
    case 3:
      return `${number}rd`;
    default:
      return `${number}th`;
  }
}

export {
  decimalToPercent,
  toCompact,
  toCurrency,
  toDecimal,
  toDuration,
  toOrdinal,
};
